import { ReactNode, useMemo } from "react";
import UserContext from "./UserContext";
import { User } from "../model/User";
import {
    useGetUserQuery,
    useGetWorkspaceIntegrationsQuery,
    useGetWorkspacesQuery,
} from "@/services/apis/api.ts";
import DisabledUiWithPricingTable from "../common/DisabledUiWithPricingTable";
import { IdentifiedUser } from "@/app/IdentifiedUser.tsx";
import { FeatureFlagManagerProvider } from "@/services/featureFlags/FeatureFlagManagerProvider.tsx";
import PasskeyEnrollmentPrompt from "@/app/PasskeyEnrollmentPrompt.tsx";
import TermsAcceptanceGuard from "@/features/Terms/TermsAcceptanceGuard.tsx";
import { useGetWorkspaceMetaQuery } from "@/services/apis/data.ts";
import { useSelector } from "react-redux";
import { getSelectedWorkspace } from "@/features/Workspace/Workspace.slice.ts";
import Panel from "@/common/Panel/Panel.tsx";
import Button from "@/common/Button";
import { Logomark } from "@/common/Logo/Logo.tsx";
import { useSignOut } from "@/hooks/use-signout.ts";

const exemptRoutes = ["/client-manager"];

export const RequiredDataProvider = ({
    children,
}: {
    children: (isLoading: boolean, user?: User) => ReactNode;
}) => {
    const { data: user, isLoading } = useGetUserQuery(undefined);
    // Don't use usSelectedWorkspace here as workspace might not be set
    const selectedWorkspace = useSelector(getSelectedWorkspace);

    // Prefetches, in parallel with getUser: App and the route components
    // subscribe to these same queries later, but waiting until they mount
    // would serialize the fetches behind /user and the route chunk load.
    useGetWorkspacesQuery(undefined);
    useGetWorkspaceIntegrationsQuery(undefined, {
        skip: !selectedWorkspace?.id,
    });
    const { isLoading: isLoadingWorkspaceMeta } = useGetWorkspaceMetaQuery(
        {
            workspaceId: selectedWorkspace?.id || "foo",
        },
        { skip: !selectedWorkspace?.id },
    );

    // Memoized so consumers of UserContext don't re-render every time this
    // provider does
    const userModel = useMemo(
        () => (user ? User.fromApiUser(user) : undefined),
        [user],
    );

    if (!userModel) {
        return children(isLoading || isLoadingWorkspaceMeta);
    }

    // The Api refuses everything else to a member whose access was turned off
    // (WorkspaceOrganizationContextListener), so say so instead of an app full
    // of errors.
    if (
        !userModel.currentUserActiveInOrganization &&
        !userModel.isSuperAdmin()
    ) {
        return (
            <AccessTurnedOff
                organizationName={userModel.currentOrganization.name}
            />
        );
    }

    if (
        userModel.currentOrganization.needsSubscriptionSetUp &&
        // Super admins can browse expired/canceled accounts (a warning bar is
        // shown instead) rather than being blocked by the pricing table.
        !userModel.isSuperAdmin() &&
        !exemptRoutes.includes(window.location.pathname)
    ) {
        return (
            <UserContext.Provider value={userModel}>
                <IdentifiedUser>
                    {/* The workspace picker in its sidebar reads the workspace limit */}
                    <FeatureFlagManagerProvider user={userModel}>
                        <DisabledUiWithPricingTable />
                    </FeatureFlagManagerProvider>
                </IdentifiedUser>
            </UserContext.Provider>
        );
    }

    return (
        <UserContext.Provider value={userModel}>
            <TermsAcceptanceGuard>
                {children(isLoading, userModel)}
                <PasskeyEnrollmentPrompt />
            </TermsAcceptanceGuard>
        </UserContext.Provider>
    );
};

const AccessTurnedOff = ({
    organizationName = "your organization",
}: {
    organizationName?: string;
}) => {
    const { signOut } = useSignOut();

    return (
        <div className={"flex flex-col items-center justify-center h-full"}>
            <Panel className={"max-w-md"}>
                <div className="flex items-center justify-center mb-8">
                    <Logomark className="h-8" />
                </div>

                <div className="text-center">
                    <h2 className="text-xl font-semibold text-zinc-950 dark:text-white mb-2">
                        Your access is turned off
                    </h2>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                        An admin of {organizationName} turned off your access.
                        Ask them to turn it back on.
                    </p>
                </div>

                <div className="mt-6 flex flex-col gap-3">
                    <Button outline onClick={signOut} className="w-full">
                        Sign out
                    </Button>
                </div>
            </Panel>
        </div>
    );
};
